import React from 'react';

import {
  Box,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import ReviewStatusPill from './ReviewStatusPill';
import { money, pct } from './logic';
import { moneyColour, reviewStatusSx, tcc } from './palette';
import { reviewOf, reviewReason } from './reviewLogic';
import { forecastText, missingAssessmentInputs, outcomeScenario, points } from './reviewPresentation';
import type { CommandDeparture, CostBasis } from './types';

const cardSx = { p: 2, minWidth: 0, borderRadius: 2 };
const pillSx = { height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 }, typography: 'body2' };
const tableSx = {
  '& .MuiTableCell-root': { typography: 'body2', verticalAlign: 'top', px: 1.5, py: 1 },
  '& .MuiTableCell-head': { fontWeight: 600, bgcolor: 'action.hover', color: 'text.primary' },
  '@media (max-width: 599px)': {
    minWidth: 0,
    '&, & > tbody': { display: 'block', width: '100%' },
    '& > thead': { display: 'none' },
    '& > tbody > tr': {
      display: 'block',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      mb: 1,
      p: 1,
    },
    '& > tbody > tr > th, & > tbody > tr > td': {
      display: 'block',
      border: 0,
      px: 0,
      py: 0.5,
    },
    '& > tbody > tr > [data-label]::before': {
      content: 'attr(data-label) ": "',
      fontWeight: 600,
      color: 'text.secondary',
    },
  },
};
const valueColour = (value: number | null) => (value < 0 ? tcc.redText : value > 0 ? tcc.greenText : 'text.secondary');

export default function ReviewPanel({
  departure,
  basis,
  today = '',
}: {
  departure: CommandDeparture;
  basis: CostBasis;
  today?: string;
}) {
  const r = reviewOf(departure, basis);
  if (!r) return null;
  const action = r.primary_intervention;
  const reasons = r.review_reasons?.length ? r.review_reasons : (departure.review_reasons ?? []);
  const reasonTexts = reasons.map((reason) => reason.text);
  if (r.review_status === 'Watch') {
    const watchReason = reviewReason(departure, basis);
    if (watchReason && !reasonTexts.includes(watchReason)) reasonTexts.unshift(watchReason);
  }
  if (r.review_status === 'Assessment Incomplete' && !reasonTexts.length) {
    reasonTexts.push(...missingAssessmentInputs(departure, basis));
  }
  if (!reasonTexts.length) reasonTexts.push(action?.reason ?? 'No review reason supplied');
  const tone = reviewStatusSx(r.review_status);
  const highlight =
    r.review_status === 'Urgent' ? tcc.redText : r.review_status === 'Needs Action' ? tcc.amberText : 'divider';
  return (
    <Box component="details" sx={{ p: 1.5, minWidth: 0, overflowWrap: 'anywhere' }}>
      <Box
        component="summary"
        sx={{ cursor: 'pointer', typography: 'body2', '&::marker': { color: 'text.secondary' } }}
      >
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            maxWidth: '100%',
            verticalAlign: 'middle',
          }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            RM review
          </Box>
          <ReviewStatusPill departure={departure} basis={basis} today={today} />
          <Box component="span">{action ? `Suggested: ${forecastText(action.name)}` : 'No action'}</Box>
        </Box>
      </Box>
      <Stack spacing={1.5} sx={{ mt: 1.5, minWidth: 0 }}>
        <Card
          variant="outlined"
          sx={{
            ...cardSx,
            bgcolor: r.review_status === 'Urgent' || r.review_status === 'Needs Action' ? tone.bgcolor : 'action.hover',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Why it’s flagged
          </Typography>
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
            {reasonTexts.map((text, index) => (
              <Chip key={index} label={forecastText(text)} size="small" sx={{ ...pillSx, ...tone }} />
            ))}
          </Stack>
        </Card>
        {action && (
          <Card variant="outlined" sx={cardSx}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Options
            </Typography>
            <Stack spacing={1}>
              {[action, ...action.alternatives].map((option, index) => (
                <Box
                  key={`${option.code}-${index}`}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: index === 0 ? highlight : 'divider',
                    borderRadius: 1,
                    bgcolor: index === 0 ? tone.bgcolor : 'transparent',
                  }}
                >
                  <Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" gap={1}>
                    <Typography variant="body2" fontWeight={500}>
                      {forecastText(option.name)}
                    </Typography>
                    {option.code && <Chip size="small" label={option.code} variant="outlined" />}
                    {index === 0 && <Chip size="small" label="Suggested" sx={tone} />}
                  </Stack>
                  {index === 0 && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Why: {forecastText(action.reason)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              You choose. Nothing is applied automatically.
            </Typography>
          </Card>
        )}
        <Card variant="outlined" sx={cardSx}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            What extra bookings would do
          </Typography>
          <TableContainer
            tabIndex={0}
            role="region"
            aria-label="Conditional margin outcomes"
            sx={{ overflowX: 'auto' }}
          >
            <Table size="small" aria-label="Conditional margin outcomes" sx={{ ...tableSx, minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  {['Scenario', 'Travellers', 'Margin', 'Change', 'Short of $100 minimum', 'Short of 10% minimum'].map(
                    (label) => (
                      <TableCell key={label}>{label}</TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {r.outcomes.map((out) => {
                  const scenario = outcomeScenario(out, departure);
                  return (
                    <TableRow key={out.label}>
                      <TableCell component="th" scope="row" data-label="Scenario">
                        {scenario.label}
                      </TableCell>
                      {out.status === 'available' ? (
                        <>
                          <TableCell data-label="Travellers">{scenario.travellers}</TableCell>
                          <TableCell data-label="Margin">
                            <Box sx={{ color: moneyColour(out.margin) }}>{money(out.margin)}</Box>
                            <Box sx={{ color: valueColour(out.margin_pct) }}>{pct(out.margin_pct)}</Box>
                          </TableCell>
                          <TableCell data-label="Change">
                            <Box sx={{ color: moneyColour(out.change_dollars) }}>{money(out.change_dollars)}</Box>
                            <Box sx={{ color: valueColour(Number(out.change_pp?.toFixed(1))) }}>
                              {points(out.change_pp)}
                            </Box>
                          </TableCell>
                          <TableCell data-label="Short of $100 minimum">
                            {out.dollar_gap === 0 ? (
                              <Chip size="small" label="Met" sx={{ color: tcc.greenText, ...pillSx }} />
                            ) : (
                              <Box sx={{ color: moneyColour(out.dollar_gap) }}>{money(out.dollar_gap)}</Box>
                            )}
                          </TableCell>
                          <TableCell data-label="Short of 10% minimum">
                            {out.rate_gap_pp === 0 ? (
                              <Chip size="small" label="Met" sx={{ color: tcc.greenText, ...pillSx }} />
                            ) : (
                              <Box sx={{ color: valueColour(out.rate_gap_pp) }}>{points(out.rate_gap_pp)}</Box>
                            )}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell colSpan={5} data-label="Outcome">
                          {out.status === 'information missing'
                            ? 'Information missing'
                            : 'Not achievable within capacity'}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Twin rooms not checked · Outcomes assume the extra bookings happen
            {action?.remaining_risk ? ` · Remaining risk: ${forecastText(action.remaining_risk)}` : ''}
            {action?.review_trigger ? ` · ${forecastText(action.review_trigger)}` : ''}
          </Typography>
        </Card>
        <Card variant="outlined" sx={cardSx}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Discount check
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Extra travellers needed for the discount to pay for itself. This does not predict extra bookings.
          </Typography>
          <TableContainer tabIndex={0} role="region" aria-label="Discount check" sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Discount check" sx={{ ...tableSx, minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  {[
                    'Discount',
                    'Extra travellers needed (Main forecast)',
                    'Extra travellers needed (Lower forecast)',
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {r.discounts.map((discount) => (
                  <TableRow key={discount.discount}>
                    <TableCell data-label="Discount">
                      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} alignItems="center">
                        {pct(discount.discount)}
                        {discount.withhold && (
                          <Chip size="small" label="Withhold" sx={{ ...pillSx, color: tcc.amberText }} />
                        )}
                      </Stack>
                    </TableCell>
                    {(['P50', 'P10'] as const).map((forecast) => {
                      const c = discount.cases.find((entry) => entry.forecast === forecast);
                      return (
                        <TableCell
                          key={forecast}
                          data-label={`Extra travellers needed (${forecast === 'P50' ? 'Main' : 'Lower'} forecast)`}
                        >
                          {c?.status === 'available'
                            ? c.extra_travellers
                            : c?.status === "can't pay for itself within capacity"
                              ? 'Cannot pay for itself within capacity'
                              : 'Information missing'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        {r.dilution && (
          <Card variant="outlined" sx={cardSx}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Supplier cost dilution
            </Typography>
            <Typography variant="body2">
              Booked {pct(r.dilution.booked_rate)} · Main forecast {pct(r.dilution.predicted_rate)}. Cost step +
              {pct(r.dilution.step_size)} at {r.dilution.step_group_size} travellers.
            </Typography>
          </Card>
        )}
        {!!r.hide_candidates.length && (
          <Card variant="outlined" sx={cardSx}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Hide candidates
            </Typography>
            <Stack spacing={1.5}>
              {r.hide_candidates.map((c) => (
                <Box key={c.dep_id}>
                  <Typography variant="body2">
                    {forecastText(c.tour_name)} · {forecastText(c.dep_name)} · {c.reserved_seats} seats reserved
                  </Typography>
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 0, listStyle: 'none' }}>
                    {c.checks.map((check, index) => (
                      <Typography component="li" variant="body2" key={index}>
                        ✓ {forecastText(check)}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Candidates for a person to check. Twin rooms not checked. Nothing is hidden automatically.
            </Typography>
          </Card>
        )}
      </Stack>
    </Box>
  );
}
